import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion2';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { MessageCircleQuestionMark } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { updateEvergreenChatConfiguration } from '@/features/webinar/webinarThunks';
import type { EvergreenChatMode } from '@/features/webinar/types';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Loader from '@/components/ui/Loader';
import { cn } from '@/lib/utils';

const CONFIG_ACCORDION_VALUE = 'item-1';

type ChatConfigurationSectionProps = {
  accordionValue?: string;
  onAccordionValueChange?: (value: string) => void;
  onSaveSuccess?: () => void;
  isEdit?: boolean;
  readOnly?: boolean;
};

export default function ChatConfigurationSection({
  accordionValue: controlledValue,
  onAccordionValueChange,
  onSaveSuccess,
  isEdit = false,
  readOnly,
}: ChatConfigurationSectionProps = {}) {
  const locked = readOnly === true;
  const { t } = useTranslation('common');
  const { t: tCW } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state: RootState) => state.workspace.currentWorkspace?.id);
  const { currentEvergreenChatConfiguration, evergreenChatConfigurationLoading } = useAppSelector(
    (state: RootState) => state.webinar,
  );
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const webinarId = typeof webinarIdParam === 'string' ? parseInt(webinarIdParam, 10) : undefined;

  const [internalAccordionValue, setInternalAccordionValue] = useState(CONFIG_ACCORDION_VALUE);
  const isControlled = controlledValue !== undefined;
  const accordionValue = isControlled ? controlledValue : internalAccordionValue;
  const setAccordionValue = isControlled
    ? (onAccordionValueChange ?? (() => {}))
    : setInternalAccordionValue;

  const [chatMode, setChatMode] = useState<EvergreenChatMode>('LIVE_ACTIVE');
  const [publicChatVisible, setPublicChatVisible] = useState(false);
  const [recordAttendeeMessages, setRecordAttendeeMessages] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (currentEvergreenChatConfiguration) {
      setChatMode(currentEvergreenChatConfiguration.chatMode);
      setPublicChatVisible(currentEvergreenChatConfiguration.publicChatVisible);
      setRecordAttendeeMessages(currentEvergreenChatConfiguration.recordAttendeeMessages);
    }
  }, [currentEvergreenChatConfiguration]);

  const handleSave = async () => {
    if (locked) return;
    if (!workspaceId || !webinarId || isNaN(webinarId)) return;
    setIsSubmitting(true);
    const result = await dispatch(
      updateEvergreenChatConfiguration({
        workspaceId,
        webinarId,
        input: {
          chatMode,
          publicChatVisible,
          recordAttendeeMessages,
        },
      }),
    );
    setIsSubmitting(false);
    if (updateEvergreenChatConfiguration.fulfilled.match(result)) {
      toast.success(tCW('chatConfig.saveSuccess'));
      onSaveSuccess?.();
    } else if (updateEvergreenChatConfiguration.rejected.match(result)) {
      const error = result.payload as { message?: string } | undefined;
      const errorMessage = error?.message
        ? t(error.message, 'Une erreur est survenue lors de la sauvegarde.')
        : t('errors.generic', 'Une erreur est survenue lors de la sauvegarde.');
      toast.error(errorMessage);
    }
  };

  const chatModeValue = chatMode === 'LIVE_ACTIVE' ? 'enabled' : 'disabled';

  return (
    <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue}>
      <AccordionItem
        value={CONFIG_ACCORDION_VALUE}
        className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}
      >
        <AccordionTrigger className="rounded-[20px]" fillForm>
          {tCW('chatConfig.accordionTitle')}
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-6 ">
          <div
            className={cn(
              'flex flex-col gap-6',
              locked && 'pointer-events-none select-none opacity-90',
            )}
          >
            <div className="pt-2">
              <RadioGroup
                value={chatModeValue}
                onValueChange={(v) => setChatMode(v === 'enabled' ? 'LIVE_ACTIVE' : 'DISABLED')}
                className="flex items-center gap-4"
                disabled={locked || evergreenChatConfigurationLoading}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="enabled" id="chat-enabled" />
                  <Label htmlFor="chat-enabled" className="cursor-pointer font-normal">
                    {tCW('chatConfig.chatEnabled')}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="disabled" id="chat-disabled" />
                  <Label htmlFor="chat-disabled" className="cursor-pointer font-normal">
                    {tCW('chatConfig.chatDisabled')}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="flex items-center justify-between gap-2">
              <Label>
                {tCW('chatConfig.publicChatLabel')}{' '}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-secondary cursor-help inline-flex">
                      <MessageCircleQuestionMark size={24} />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-sm">
                    <p>{tCW('chatConfig.publicChatTooltip')}</p>
                  </TooltipContent>
                </Tooltip>
              </Label>
              <Switch
                checked={publicChatVisible}
                onCheckedChange={setPublicChatVisible}
                disabled={locked || evergreenChatConfigurationLoading}
              />
            </div>

            <div className="flex items-center justify-between gap-2">
              <Label>
                {tCW('chatConfig.recordMessagesLabel')}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-secondary cursor-help inline-flex">
                      <MessageCircleQuestionMark size={24} />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-sm">
                    <p>{tCW('chatConfig.recordMessagesTooltip')}</p>
                  </TooltipContent>
                </Tooltip>
              </Label>
              <Switch
                checked={recordAttendeeMessages}
                onCheckedChange={setRecordAttendeeMessages}
                disabled={locked || evergreenChatConfigurationLoading}
              />
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={locked || evergreenChatConfigurationLoading || isSubmitting}
          >
            {isSubmitting && <Loader />} {tCW('save')}
          </Button>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
